/* ============================================================================
   SL PROOF PAGE — the DOM half of the proof review

   `sl-proof-review.js` decides WHAT to show and owns the transport.
   This file paints it. The split is deliberate: every rule worth testing lives
   in the other file and is testable in Node, while this one is the part that
   needs a browser and has no rules of its own.

   ── ORDER AND DESIGN COME FROM THE URL ───────────────────────────────────
   `?order=...&design=...`. They are identifiers, not capabilities: the proof
   they name is only returned to the signed-in customer who owns it, and the
   server answers 404 to everyone else. Putting them in the URL is what makes
   the Klaviyo deep link -- and "log in and come back to this exact proof" --
   possible without a token in an email.
   ============================================================================ */
(function () {
  'use strict';

  var R = window.SLProofReview;
  var root = document.getElementById('slProof');
  if (!R || !root) return;

  var body = document.getElementById('slpBody');
  var orderEl = document.getElementById('slpOrder');

  var q = new URLSearchParams(window.location.search);
  var orderId = q.get('order') || '';
  var designId = q.get('design') || '';
  var endpoint = root.getAttribute('data-endpoint') || '';

  var client = null;
  var current = null;          // the payload we last rendered
  var noteValue = '';

  function h(html) { body.innerHTML = html; }

  /* ---- shared fragments ------------------------------------------------- */

  function steps(active) {
    var names = ['Review', 'Your notes', 'Confirm'];
    var out = '<div class="slp-steps">';
    names.forEach(function (n, i) {
      var cls = i < active ? 'done' : (i === active ? 'on' : '');
      out += '<span class="slp-step ' + cls + '"><b>' + (i + 1) + '</b>' + R.esc(n) + '</span>';
      if (i < names.length - 1) out += '<span class="slp-rule"></span>';
    });
    return out + '</div>';
  }

  function tick(wait) {
    return '<div class="slp-tick' + (wait ? ' slp-tick--wait' : '') + '">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      (wait ? '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>' : '<path d="M20 6 9 17l-5-5"/>') +
      '</svg></div>';
  }

  /* ── WHERE MY STICKR LAB ACTUALLY IS ──────────────────────────────────
     `/pages/my-stickr-lab` is not a page. The workspace is the index page
     rendered with its alternate template (`templates/index.mysl.json`),
     which Shopify reaches as `/?view=mysl` -- the same literal the header,
     the home page, the footer, the control app and the builder all use.

     It was a plausible-looking path that had simply never been clicked:
     four of the five screens below link here, and the fifth is the one a
     customer reaches immediately after approving. */
  var MYSL_PATH = '/?view=mysl';

  function backActions(primaryLabel) {
    return '<div class="slp-actions">' +
      '<a class="slp-btn slp-btn--go" href="' + MYSL_PATH + '">' + R.esc(primaryLabel) + '</a>' +
      '</div>';
  }

  /* ---- screens ---------------------------------------------------------- */

  function renderReview(p) {
    var specs = R.specLines(p);
    /* ── THE PICTURE THE CUT LINE IS DRAWN ON ────────────────────────
       When the production raster is published for this revision, it is
       fetched from our own service and it IS the cut frame: the production
       file places it at x=0 y=0 w=480 h=388, which is the viewBox the cut
       path is expressed in. `contain` and `meet` then land both on the
       identical rect, with no scaling factor anywhere in this file.

       When it is not published, `p.artwork` is the padded checkout preview.
       That picture is fine to SHOW and wrong to MEASURE against, so the cut
       overlay is withheld rather than fitted independently -- which is what
       made a 480x388 frame render 1.24x the width of a 969x969 square. */
    var aligned = p.artworkAligned === true;
    /* A MANUAL PROOF is the image our team uploaded, served by the same
       artwork route. It is never aligned, so it never gets an overlay. */
    var manual = R.isManual(p);
    var artSrc = (aligned || manual) ? client.artworkUrl() : p.artwork;
    var art = artSrc
      ? '<img class="slp-art" src="' + R.esc(artSrc) + '" alt="' +
        (manual ? 'Your artwork proof' : 'Your artwork') + '">'
      : '';
    var overlay = aligned ? R.overlaySvg(p.cutLine) : '';
    if (manual) overlay = '';

    h(
      steps(0) +
      '<div class="slp-grid">' +

        '<section class="slp-card slp-proofcard">' +
          '<div class="slp-head">' +
            '<span class="slp-eyebrow">Your artwork proof</span>' +
            '<h1>Review your proof</h1>' +
            /* WHICH proof. A customer who asked for changes has seen more than
               one, and the approval they give is recorded against this number. */
            (p.revision && p.revision.version
              ? '<span class="slp-ver">Proof version ' + R.esc(String(p.revision.version)) + '</span>'
              : '') +
            (manual
              ? '<p>Check the proof our team prepared for you. If everything looks good, ' +
                'approve it. If you&rsquo;d like a change, tell us what to change.</p>'
              : '<p>Check your artwork and cut line. If everything looks good, approve your ' +
                'proof. If you&rsquo;d like a change, tell us what to change.</p>') +
          '</div>' +
          '<div class="slp-stage-wrap">' +
            /* ZOOM MOVES ONE BOX. The artwork and the cut line both fill
               `.slp-zoom`, and zooming resizes that box alone -- so the two
               layers cannot drift apart at any level, and the stage scrolls
               natively (drag on a phone, wheel or scrollbars on a desktop). */
            '<div class="slp-stage" id="slpStage"><div class="slp-zoom" id="slpZoom">' + art + overlay + '</div></div>' +
            '<div class="slp-zoombar" role="group" aria-label="Zoom">' +
              '<button type="button" class="slp-zbtn" id="slpZoomOut" aria-label="Zoom out">&minus;</button>' +
              '<span class="slp-zpct" id="slpZoomPct" aria-live="polite">100%</span>' +
              '<button type="button" class="slp-zbtn" id="slpZoomIn" aria-label="Zoom in">+</button>' +
              '<button type="button" class="slp-zreset" id="slpZoomReset">Fit</button>' +
            '</div>' +
            '<div class="slp-legend">' +
              '<span class="slp-key"><i class="slp-dot slp-dot--art"></i>' +
                (manual ? 'Artwork proof' : 'Artwork') + '</span>' +
              (overlay
                ? '<span class="slp-key"><i class="slp-dot slp-dot--cut"></i>Cut line</span>'
                : '') +
            '</div>' +
            /* Says what the line IS, before they judge it.
               This used to promise that the line would be tidied up for them
               afterwards. That described an older workflow. The line drawn
               here is the production cut line, snapshotted into the revision
               and unchanged from the file the cutter will use -- approving it
               approves exactly that. Suggesting it would still change invited
               a customer to accept it on a promise we no longer keep. */
            (manual
              /* No cut line is drawn, and none is promised: the image is the
                 proof, so the note says that and nothing about a line. */
              ? '<div class="slp-cutnote">' +
                  '<b>Proof</b>' +
                  '<span>Prepared by our team for this order. Please check it ' +
                  'carefully before you approve.</span>' +
                '</div>'
              : overlay
              ? '<div class="slp-cutnote">' +
                  '<b>Cut line</b>' +
                  '<span>This is the exact line your stickers will be cut on.</span>' +
                '</div>'
              /* Said plainly rather than shown wrongly. */
              : '<div class="slp-cutnote">' +
                  '<b>Cut line</b>' +
                  '<span>Your cut line is still being prepared. This is your ' +
                  'artwork as you sent it.</span>' +
                '</div>') +
          '</div>' +
        '</section>' +

        '<aside class="slp-side">' +
          /* Our message for THIS version sits first in the rail, so on a
             phone it is read before the two decisions. */
          R.messageHtml(p.revision) +
          (specs.length
            ? '<div class="slp-card slp-panel"><h2>Order details</h2><dl class="slp-specs">' +
              specs.map(function (s) {
                return '<div class="slp-spec"><dt>' + R.esc(s.key) + '</dt>' +
                       '<dd>' + R.esc(s.value) + '</dd></div>';
              }).join('') + '</dl></div>'
            : '') +

          '<div class="slp-card slp-panel slp-note">' +
            '<div class="slp-actions" id="slpDecide">' +
              '<button type="button" class="slp-btn slp-btn--go" id="slpApprove">' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" ' +
                'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>' +
                'Approve proof</button>' +
              '<button type="button" class="slp-btn slp-btn--alt" id="slpChanges" ' +
                'aria-expanded="false" aria-controls="slpRevise">Request changes</button>' +
            '</div>' +
            /* ── A CHANGE REQUEST ALWAYS SAYS WHAT TO CHANGE ──────────────
               Request changes only opens this form; the request itself is
               the Send button, and it refuses an empty note before anything
               is sent. Approve is hidden while the form is open, so the one
               decision cannot be made while typing the other. */
            '<div class="slp-revise" id="slpRevise" hidden>' +
              '<label for="slpNote">What would you like us to change?</label>' +
              '<textarea id="slpNote" maxlength="' + R.MAX_NOTE + '" required aria-required="true" ' +
                'aria-describedby="slpErr" ' +
                'placeholder="For example: make the text bigger, or remove the white border."></textarea>' +
              '<span class="slp-count" id="slpCount">0/' + R.MAX_NOTE + '</span>' +
              '<p class="slp-err" id="slpErr" role="alert" hidden></p>' +
              '<div class="slp-actions">' +
                '<button type="button" class="slp-btn slp-btn--warn" id="slpSend">Send revision request</button>' +
                '<button type="button" class="slp-btn slp-btn--ghost" id="slpCancel">Cancel</button>' +
              '</div>' +
            '</div>' +
          '</div>' +
        '</aside>' +

        /* Under the proof, and after the decisions on a phone. */
        R.threadHtml(p.thread) +
      '</div>'
    );

    var note = document.getElementById('slpNote');
    var count = document.getElementById('slpCount');
    var decide = document.getElementById('slpDecide');
    var revise = document.getElementById('slpRevise');
    var changes = document.getElementById('slpChanges');
    note.value = noteValue;
    count.textContent = noteValue.length + '/' + R.MAX_NOTE;
    note.addEventListener('input', function () {
      noteValue = note.value;
      count.textContent = note.value.length + '/' + R.MAX_NOTE;
      hideError();
    });

    wireZoom();

    function openRevise(open) {
      revise.hidden = !open;
      decide.hidden = open;
      changes.setAttribute('aria-expanded', open ? 'true' : 'false');
      hideError();
      if (open) note.focus();
      else changes.focus();
    }

    document.getElementById('slpApprove').addEventListener('click', function () {
      submit('approve', null);
    });
    changes.addEventListener('click', function () { openRevise(true); });
    document.getElementById('slpCancel').addEventListener('click', function () { openRevise(false); });
    document.getElementById('slpSend').addEventListener('click', function () {
      submit('request_changes', note.value);
    });
    if (noteValue) openRevise(true);
  }

  /* 100% to 400%. The level is the size of `.slp-zoom` relative to the stage;
     the point at the centre of the view stays at the centre across a step. */
  var ZOOM_LEVELS = [1, 1.5, 2, 3, 4];
  function wireZoom() {
    var stage = document.getElementById('slpStage');
    var box = document.getElementById('slpZoom');
    var pct = document.getElementById('slpZoomPct');
    var out = document.getElementById('slpZoomOut');
    var inn = document.getElementById('slpZoomIn');
    var reset = document.getElementById('slpZoomReset');
    if (!stage || !box || !pct || !out || !inn || !reset) return;
    var at = 0;
    function apply(next) {
      var before = ZOOM_LEVELS[at], after = ZOOM_LEVELS[next];
      var cx = (stage.scrollLeft + stage.clientWidth / 2) / before;
      var cy = (stage.scrollTop + stage.clientHeight / 2) / before;
      at = next;
      box.style.width = (after * 100) + '%';
      box.style.height = (after * 100) + '%';
      stage.className = 'slp-stage' + (after > 1 ? ' slp-stage--zoomed' : '');
      stage.scrollLeft = cx * after - stage.clientWidth / 2;
      stage.scrollTop = cy * after - stage.clientHeight / 2;
      pct.textContent = Math.round(after * 100) + '%';
      out.disabled = at === 0;
      inn.disabled = at === ZOOM_LEVELS.length - 1;
      reset.disabled = at === 0;
    }
    out.addEventListener('click', function () { if (at > 0) apply(at - 1); });
    inn.addEventListener('click', function () { if (at < ZOOM_LEVELS.length - 1) apply(at + 1); });
    reset.addEventListener('click', function () { apply(0); });
    apply(0);
  }

  function renderApproved(p) {
    h(steps(3) +
      '<section class="slp-card slp-result">' +
        tick(false) +
        '<h1>Proof approved!</h1>' +
        '<p>Thanks! Your stickers are ready for production.</p>' +
        /* NOT "View order". There is no order page: the Orders entry inside
           My Stickr Lab is one of the prototype sections that is hidden
           precisely because it shows nothing real. The button goes where it
           actually goes, and says so -- the same words the other four
           screens use. */
        backActions('Back to My Stickr Lab') +
      '</section>' +
      /* The whole conversation stays readable after the decision. */
      threadBlock(p));
  }

  function renderRequested(p) {
    var note = p.revision && p.revision.note;
    var thread = threadBlock(p);
    h(steps(2) +
      '<section class="slp-card slp-result slp-result--warn">' +
        tick(true) +
        /* It has to SAY it arrived: the customer has just handed us
           something and is now waiting on us. */
        '<h1>Revision request received</h1>' +
        '<p>We got your note. We&rsquo;ll update your proof and let you know ' +
        'when the new version is ready.</p>' +
        /* With a thread, their note is already in it, quoted under the
           version it was about; quoting it twice is noise. */
        (note && !thread ? '<div class="slp-quote"><span>Your note</span><p>' + R.esc(note) + '</p></div>' : '') +
        backActions('Back to My Stickr Lab') +
      '</section>' +
      thread);
  }

  function threadBlock(p) {
    var t = R.threadHtml(p && p.thread);
    return t ? '<div class="slp-after">' + t + '</div>' : '';
  }

  /* NO PRINT PROMISE (Ryan, 28 Oct 2026). These two screens used to promise
     that printing waits for approval; the page asks for a review and promises
     nothing about printing. */
  var REVIEW_ASK = 'Review your artwork and let us know if anything needs adjusting.';

  function renderPreparing() {
    h('<section class="slp-card slp-result">' +
        tick(true) +
        '<h1>Your proof is on its way</h1>' +
        '<p>We&rsquo;re preparing your proof now. We&rsquo;ll let you know the moment ' +
        'it&rsquo;s ready to review. ' + REVIEW_ASK + '</p>' +
        backActions('Back to My Stickr Lab') +
      '</section>');
  }

  /* A proof whose cut line cannot be shown or no longer matches the revision
     is NOT reviewable. Approval is not offered, because approving something
     invisible is not approval. */
  function renderUnavailable() {
    h('<section class="slp-card slp-result">' +
        tick(true) +
        '<h1>This proof needs another look</h1>' +
        '<p>Your proof has been updated since this page was opened. We&rsquo;re ' +
        'preparing the new version for you to review. ' + REVIEW_ASK + '</p>' +
        backActions('Back to My Stickr Lab') +
      '</section>');
  }

  function renderSignedOut() {
    var back = window.location.pathname + window.location.search;
    h('<section class="slp-card slp-result">' +
        tick(true) +
        '<h1>Sign in to review your proof</h1>' +
        '<p>Your proof, notes and approval live in My Stickr Lab. Sign in and ' +
        'we&rsquo;ll bring you straight back here.</p>' +
        '<div class="slp-actions">' +
          '<a class="slp-btn slp-btn--go" href="' +
            R.esc(R.loginUrl(null, null, back)) + '">Sign in</a>' +
        '</div>' +
      '</section>');
  }

  function renderError(message) {
    h('<section class="slp-card slp-result">' +
        tick(true) +
        '<h1>We couldn&rsquo;t load your proof</h1>' +
        '<p>' + R.esc(message || 'Please try again in a moment.') + '</p>' +
        backActions('Back to My Stickr Lab') +
      '</section>');
  }

  /* ---- actions ---------------------------------------------------------- */

  function showError(msg) {
    var el = document.getElementById('slpErr');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }
  function hideError() {
    var el = document.getElementById('slpErr');
    if (el) el.hidden = true;
  }

  function submit(decision, note) {
    var problem = R.noteError(note, decision === 'request_changes');
    if (problem) { showError(problem); return; }

    var buttons = ['slpApprove', 'slpChanges', 'slpSend', 'slpCancel'].map(function (id) {
      return document.getElementById(id);
    }).filter(Boolean);
    function lock(on) { buttons.forEach(function (b) { b.disabled = on; }); }
    /* Disabled on the FIRST click. The server is idempotent too, but a second
       click should not even become a second request. */
    lock(true);

    var version = current && current.revision ? current.revision.version : null;

    client.decide(decision, note, version).then(function (r) {
      if (r && r.signedOut) return renderSignedOut();
      if (r && r.error) {
        lock(false);
        /* A stale or changed proof is not a failure the customer caused, so it
           reloads into the honest state rather than showing a raw error. */
        if (r.error === 'stale_revision' || r.error === 'geometry_changed' ||
            r.error === 'geometry_unavailable') {
          return load();
        }
        return showError(r.message || 'That did not go through. Please try again.');
      }
      noteValue = '';
      r = R.afterDecision(r, current, decision, note, version, Date.now());
      current = r;
      if (decision === 'approve') renderApproved(r);
      else renderRequested(r);
    });
  }

  function paint(p) {
    current = p;
    if (p && p.orderNumber && orderEl) orderEl.textContent = 'Order ' + p.orderNumber;

    switch (R.proofState(p)) {
      case R.VIEW.SIGNED_OUT:  return renderSignedOut();
      case R.VIEW.APPROVED:    return renderApproved(p);
      case R.VIEW.REQUESTED:   return renderRequested(p);
      case R.VIEW.PREPARING:   return renderPreparing();
      case R.VIEW.UNAVAILABLE: return renderUnavailable();
      case R.VIEW.REVIEW:      return renderReview(p);
      default:                 return renderError();
    }
  }

  function load() {
    return client.load().then(function (p) {
      if (p && p.error && !p.signedOut) return renderError(p.message);
      paint(p);
    });
  }

  /* ---- boot ------------------------------------------------------------- */

  if (!endpoint) {
    /* Ships dark: with no proxy configured the page says so rather than
       rendering an empty stage that looks like a broken proof. */
    renderError('Proof review is not available yet.');
    return;
  }
  if (!orderId || !designId) {
    renderError('That proof link is missing some details.');
    return;
  }

  client = R.createClient({ endpoint: endpoint, orderId: orderId, designId: designId });
  load();
})();
